import * as Sentry from '@sentry/browser'
import Agent from 'agentkeepalive'
import axios, { AxiosResponse } from 'axios'
import axiosRetry from 'axios-retry'

import {
  ListResponse,
  PerformanceListResponse,
  RawReservationCreateResponse,
} from '@/api/manual_types'
import { getVersion } from '@/api/version'
import { useSettingStore } from '@/stores/setting'
import { enhanceError, isErrorData } from '@/utils/errors'
import loc from '@/utils/loc'

import {
  AccountingReportRead,
  AddOnRead,
  ArrangementRead,
  ArrangementWrite,
  BulkPriceWrite,
  CouponRead,
  GuestDetailedRead,
  GuestRead,
  GuestSearchRead,
  GuestWrite,
  PerformanceAddOnRead,
  PerformanceAddOnWrite,
  PerformanceCreate,
  PerformanceDetailedWrite,
  PerformanceQueryParams,
  PerformanceRead,
  PerformanceReportRead,
  PerformanceReservationsRead,
  PerformanceTableRead,
  PerformanceTableWrite,
  PerformanceWrite,
  ReservationCreate,
  ReservationId,
  ReservationPaidUpdate,
  ReservationRead,
  ReservationUpdate,
  SeatingTimeRead,
  SeatingTimeWrite,
  SettingRead,
  ShowRead,
  ShowTimeRead,
  ShowTimeWrite,
  ShowWrite,
  StripeReaderId,
  StripeReaderRead,
  SuccessResponse,
  TableRead,
  TableWrite,
  TransactionRead,
  UserRead,
} from '@generated/types'

function isErrorResponse(response: AxiosResponse): boolean {
  return response.data.error && isErrorData(response.data.error)
}

export class UnauthenticatedError extends Error {}
export class ServerFailureError extends Error {}
export class BadRequestError extends Error {}
export class BackendAPIError extends Error {}
export class NoResponseError extends Error {}
export class InvalidVenueError extends Error {}

export async function handleAxiosError(error) {
  if (!axios.isAxiosError(error)) {
    Sentry.captureException(new Error('Axios had an exception not an AxiosError', { cause: error }))
  } else {
    Sentry.setContext('axiosConfig', error.config)
    Sentry.setContext('axiosRequest', error.request || {})
    if (!error.response) {
      Sentry.captureException(
        new NoResponseError(`Recieved no response in api call to ${error.config.url}`, {
          cause: error,
        }),
      )
    } else {
      const status_code = error.response.status
      Sentry.setContext('axiosResponse', error.response)
      if (!isErrorResponse(error.response)) {
        Sentry.captureException(
          new ServerFailureError(
            `Received ill-formated ${status_code} response from ${error.config.url}`,
            { cause: error },
          ),
        )
      } else {
        Sentry.setContext('ttError', error.response.data.error)

        // Standardize our handling of API responses so code above can easily tell the difference between
        // the BE rejecting an invalid request and something going wrong in typescript code,
        error = enhanceError(error, error.response.data.error)
        if (status_code == 400) {
          Sentry.captureException(
            new BadRequestError(`${error.code} in ${error.config.url}`, { cause: error }),
          )
        } else if (status_code == 401 || status_code == 403) {
          console.log(`Handling ${status_code}-${error.code} by redirecting to login`)
          return loc.redirect('/accounts/login/?next=/#' + window.location.hash)
        } else if (status_code >= 500) {
          Sentry.captureException(
            new ServerFailureError(`Recieved ${status_code} in api call to ${error.config.url}`, {
              cause: error,
            }),
          )
        } else {
          Sentry.captureException(
            new BackendAPIError(`Handled ${status_code}-${error.code} in ${error.config.url}`, {
              cause: error,
            }),
          )
        }
      }
    }
  }
  return Promise.reject(error)
}

export function configureBrowserAxios() {
  axiosRetry(axios, { retries: 2, retryDelay: axiosRetry.exponentialDelay })
  axios.interceptors.response.use(response => {
    useSettingStore().updateVenueAppVersion(response.headers['x-tt-version'])
    return response
  }, handleAxiosError)

  axios.defaults.xsrfHeaderName = 'X-CSRFToken'
  axios.defaults.xsrfCookieName = 'csrftoken'
  axios.defaults.headers['X-TT-ClientVersion'] = getVersion()
}

export function configureSsrAxiosOnce() {
  axios.defaults.httpAgent = new Agent({
    maxSockets: 50,
    maxFreeSockets: 4,
    timeout: 60000,
    freeSocketTimeout: 30000,
  })

  axios.interceptors.response.use(
    r => r,
    async error => {
      if (error.response?.status === 404) {
        throw new InvalidVenueError('Invalid Venue')
      }
      return Promise.reject(error)
    },
  )
}

export function configureSsrAxios(host: string) {
  axios.defaults.baseURL = `http://127.0.0.1/` // We're connecting directly to nginx to bypass AWS load balancer
  axios.defaults.headers.common['Host'] = host
}

export function configureDevSsrAxios(host: string) {
  axios.defaults.baseURL = host // Connect to wherever, TT_HOST=https://parker-jazz.turntabletix.com for example
}

export default {
  getSettings(): Promise<AxiosResponse<SettingRead>> {
    return axios.get(`/api/settings/`)
  },

  getAddOns(): Promise<AxiosResponse<AddOnRead[]>> {
    return axios.get<AddOnRead[]>(`/api/add-on/`)
  },

  getFloorPlans(): Promise<AxiosResponse<ArrangementRead[]>> {
    return axios.get<ArrangementRead[]>(`/api/arrangement/`)
  },

  addFloorPlan(data: ArrangementWrite) {
    return axios.post(`/api/arrangement/`, data)
  },

  deleteFloorPlan(id: number) {
    return axios.delete(`/api/arrangement/${id}/`)
  },

  updateFloorPlan(data: ArrangementWrite): Promise<AxiosResponse<ArrangementRead>> {
    return axios.put(`/api/arrangement/${data.id}/`, data)
  },

  duplicateFloorPlan(id: number): Promise<AxiosResponse<ArrangementRead>> {
    return axios.post(`/api/arrangement/${id}/duplicate/`)
  },

  addFloorPlanTable(data): Promise<AxiosResponse<TableRead>> {
    return axios.post(`/api/arrangement-table/`, data)
  },

  deleteFloorPlanTable(id: number) {
    return axios.delete(`/api/arrangement-table/${id}/`)
  },

  updateFloorPlanTable(data: TableWrite): Promise<AxiosResponse<TableRead>> {
    return axios.put(`/api/arrangement-table/${data.id}/`, data)
  },

  bulkPriceFloorPlanTables(
    floorPlanId,
    data: BulkPriceWrite,
  ): Promise<AxiosResponse<SuccessResponse>> {
    return axios.put(`/api/arrangement/${floorPlanId}/bulk-price/`, data)
  },

  addPerformanceTable(data: PerformanceTableWrite): Promise<AxiosResponse<PerformanceTableRead>> {
    return axios.post(`/api/performance-table/`, data)
  },

  deletePerformanceTable(id: number) {
    return axios.delete(`/api/performance-table/${id}/`)
  },

  updatePerformanceTable(
    data: PerformanceTableWrite,
  ): Promise<AxiosResponse<PerformanceTableRead>> {
    return axios.put(`/api/performance-table/${data.id}/`, data)
  },

  bulkPricePerformanceTables(
    performanceId,
    data: BulkPriceWrite,
  ): Promise<AxiosResponse<SuccessResponse>> {
    return axios.put(`/api/performance/${performanceId}/bulk-price/`, data)
  },

  getShowTimes(): Promise<AxiosResponse<ShowTimeRead[]>> {
    return axios.get(`/api/showtime/`)
  },

  addShowTime(data: ShowTimeWrite): Promise<AxiosResponse<ShowTimeRead>> {
    return axios.post(`/api/showtime/`, data)
  },

  updateShowTime(data: ShowTimeWrite): Promise<AxiosResponse<ShowTimeRead>> {
    return axios.put(`/api/showtime/${data.id}/`, data)
  },

  deleteShowTime(id: number) {
    return axios.delete(`/api/showtime/${id}/`)
  },

  addSeatingTime(data: SeatingTimeWrite): Promise<AxiosResponse<SeatingTimeRead>> {
    return axios.post(`/api/seating-time/`, data)
  },

  updateSeatingTime(data: SeatingTimeWrite): Promise<AxiosResponse<SeatingTimeRead>> {
    return axios.put(`/api/seating-time/${data.id}/`, data)
  },

  getShow(id: number): Promise<AxiosResponse<ShowRead>> {
    return axios.get(`/api/show/${id}/`)
  },

  getShows(): Promise<AxiosResponse<ShowRead[]>> {
    return axios.get(`/api/show/`)
  },

  addShow(data: ShowWrite): Promise<AxiosResponse<ShowRead>> {
    return axios.post(`/api/show/`, data)
  },

  updateShow(data: ShowWrite): Promise<AxiosResponse<ShowRead>> {
    return axios.put(`/api/show/${data.id}/`, data)
  },

  updateShowImage(id: number, data): Promise<AxiosResponse<ShowRead>> {
    return axios.put(`/api/show/${id}/set_image/`, data)
  },

  updateShowImagePosition(id, data): Promise<AxiosResponse<ShowRead>> {
    return axios.put(`/api/show/${id}/set_image_position/`, data)
  },

  deleteShow(id: number) {
    return axios.delete(`/api/show/${id}/`)
  },

  duplicateShow(id: number): Promise<AxiosResponse<ShowRead>> {
    return axios.post(`/api/show/${id}/duplicate/`)
  },

  getPerformances(query: PerformanceQueryParams): Promise<AxiosResponse<PerformanceListResponse>> {
    return axios.get(
      `/api/performance/?${Object.entries(query)
        .map(entry => entry[0] + '=' + entry[1])
        .join('&')}`,
    )
  },

  getPerformanceReports(
    query: PerformanceQueryParams,
  ): Promise<AxiosResponse<PerformanceReportRead[]>> {
    return axios.get(
      `/api/performance/report/?${Object.entries(query)
        .map(entry => entry[0] + '=' + entry[1])
        .join('&')}`,
    )
  },

  getAccountingSummary(
    month: string,
    type: 'accrual' | 'cash',
  ): Promise<AxiosResponse<AccountingReportRead[]>> {
    return axios.get(`/api/accounting/${type}/?months=${month}`)
  },

  getPerformance(id: number): Promise<AxiosResponse<PerformanceRead>> {
    return axios.get(`/api/performance/${id}/`)
  },

  addPerformance(data: PerformanceCreate): Promise<AxiosResponse<PerformanceRead>> {
    return axios.post(`/api/performance/`, data)
  },

  updatePerformance(data: PerformanceWrite): Promise<AxiosResponse<PerformanceRead>> {
    return axios.put(`/api/performance/${data.id}/`, data)
  },

  updatePerformanceDetailed(
    data: PerformanceDetailedWrite,
  ): Promise<AxiosResponse<PerformanceRead>> {
    return axios.put(`/api/performance/${data.id}/detailed/`, data)
  },

  deletePerformance(id: number) {
    return axios.delete(`/api/performance/${id}/`)
  },

  copyAddOnToPerformance({
    performance_id,
    add_on_id,
  }: {
    performance_id: number
    add_on_id: number
  }): Promise<AxiosResponse<PerformanceAddOnRead>> {
    return axios.post(`/api/performance/${performance_id}/add-on/from-add-on/${add_on_id}/`, {})
  },

  addPerformanceAddOn(
    performance_id,
    data: PerformanceAddOnWrite,
  ): Promise<AxiosResponse<PerformanceAddOnRead>> {
    return axios.post(`/api/performance/${performance_id}/add-on/`, data)
  },

  updatePerformanceAddOn(
    performance_id,
    data: PerformanceAddOnWrite,
  ): Promise<AxiosResponse<PerformanceAddOnRead>> {
    return axios.put(`/api/performance/${performance_id}/add-on/${data.id}/`, data)
  },

  deletePerformanceAddOn({
    performance_id,
    add_on_id,
  }: {
    performance_id: number
    add_on_id: number
  }) {
    return axios.delete(`/api/performance/${performance_id}/add-on/${add_on_id}/`)
  },

  getReservations(): Promise<AxiosResponse<ListResponse<ReservationRead>>> {
    return axios.get(`/api/reservation/`)
  },

  getReservation(id: number): Promise<AxiosResponse<ReservationRead>> {
    return axios.get(`/api/reservation/${id}/`)
  },

  getPerformanceReservations(id: number): Promise<AxiosResponse<PerformanceReservationsRead>> {
    return axios.get(`/api/performance/${id}/reservations/`)
  },

  addReservation(data: ReservationCreate): Promise<AxiosResponse<RawReservationCreateResponse>> {
    return axios.post(`/api/reservation/`, data)
  },

  retryReservation(id: ReservationId, reader_id?: StripeReaderId): Promise<AxiosResponse> {
    const query = reader_id ? `?reader_id=${reader_id}` : ''
    return axios.post(`/api/reservation/${id}/retry/${query}`)
  },

  updateReservation(data: ReservationUpdate): Promise<AxiosResponse<ReservationRead>> {
    return axios.put(`/api/reservation/${data.id}/`, data)
  },

  paidUpdateReservation(data: ReservationPaidUpdate): Promise<AxiosResponse<ReservationRead>> {
    return axios.put(`/api/reservation/${data.id}/`, data)
  },

  payReservation(
    id: ReservationId,
    reader_id: StripeReaderId,
  ): Promise<AxiosResponse<SuccessResponse>> {
    return axios.post(`/api/reservation/${id}/pay/`, { reader_id })
  },

  refundTransaction(data: {
    transactionId: number
    amount: number
  }): Promise<AxiosResponse<TransactionRead>> {
    return axios.post(`/api/transaction/${data.transactionId}/refund/`, { amount: data.amount })
  },

  resendConfirmationEmail(id: number, email: string): Promise<AxiosResponse> {
    return axios.post(`/api/reservation/${id}/resend-confirmation/`, { email })
  },

  deleteReservation(id: number) {
    return axios.delete(`/api/reservation/${id}/`)
  },

  getReservationStatus(id: ReservationId): Promise<AxiosResponse<ReservationRead>> {
    return axios.get(`/api/reservation/${id}/status/`)
  },

  validatePromotionCode(code: string) {
    return axios.get(`/api/promotion-code/validate/`, { params: { code } })
  },

  getCoupons(): Promise<AxiosResponse<CouponRead[]>> {
    return axios.get(`/api/coupon/`)
  },

  searchGuests(options: {
    query: string
    field?: string
  }): Promise<AxiosResponse<GuestSearchRead[]>> {
    const params = { q: options.query }
    if (options.field) {
      params['field'] = options.field
    }
    return axios.get(`/api/guest/search/`, { params })
  },

  getGuest(id: number): Promise<AxiosResponse<GuestDetailedRead>> {
    return axios.get(`/api/guest/${id}/reservations/`)
  },

  updateGuest(data: GuestWrite): Promise<AxiosResponse<GuestRead>> {
    return axios.put(`/api/guest/${data.id}/`, data)
  },

  mergeGuest(sourceId: number, targetId: number): Promise<AxiosResponse<GuestRead>> {
    return axios.post(`/api/guest/${sourceId}/merge-into/${targetId}/`)
  },

  getMe(): Promise<AxiosResponse<UserRead>> {
    return axios.get(`/api/user/profile/`)
  },

  getUsers(): Promise<AxiosResponse<UserRead[]>> {
    return axios.get(`/api/user/`)
  },

  getReaders(): Promise<AxiosResponse<StripeReaderRead[]>> {
    return axios.get(`/api/stripe/reader/`)
  },

  cancelReaderAction(readerId: StripeReaderId): Promise<AxiosResponse<StripeReaderRead>> {
    return axios.post(`/api/stripe/reader/cancel/`, { reader_id: readerId })
  },

  simulatePayment(
    readerId: StripeReaderId,
    cardNumber: string,
  ): Promise<AxiosResponse<StripeReaderRead>> {
    return axios.post(`/api/stripe/reader/simulate/`, {
      reader_id: readerId,
      card_number: cardNumber,
    })
  },
}
